import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import type { ApplicationTypes } from '@/enums/applicationTypes.ts'
import type { IAttachmentToDownload, IGridResponse } from '@/typings/api'
import type {
    IApplication,
    IApplicationSettlementData,
    IFinancialSupportDetails,
    IGridApplicationItem,
} from '@/typings/application'
import type { IGridPayloadRequest } from '@/types/list'
import { buildQueryForGrid } from '@/helpers/grid.ts'
import { NO_CONTENT_CODE, OK_CODE } from '@/config/httpCodes.ts'
import { ApplicationStatuses } from '@/enums/applicationStatuses.ts'

export default (axios: AxiosInstance) => ({
    createNewApplication: async ({
        subAccountId,
        applicationType,
    }: {
        subAccountId: string
        applicationType: ApplicationTypes
    }): Promise<{ applicationId: string }> => {
        const { data } = await axios.post<{ applicationId: string }>(
            `/guardian/${subAccountId}/application/applications`,
            {
                applicationType,
            },
        )

        return data
    },

    getApplicationsForGrid: async ({
        subAccountId,
        gridPayload,
    }: {
        subAccountId: string
        gridPayload: IGridPayloadRequest
    }): Promise<IGridResponse<IGridApplicationItem>> => {
        const query = buildQueryForGrid(gridPayload)

        const { data } = await axios.get<IGridResponse<IGridApplicationItem>>(
            `/guardian/${subAccountId}/application/applications?${query}`,
            {
                signal: gridPayload.signal,
            },
        )

        return data
    },

    removeApplication: async ({
        subAccountId,
        applicationId,
        version,
    }: {
        subAccountId: string
        applicationId: string
        version: number
    }): Promise<boolean> => {
        const { status } = await axios.delete(
            `/guardian/${subAccountId}/application/applications/${applicationId}`,
            {
                params: {
                    version,
                },
            },
        )

        return status === NO_CONTENT_CODE
    },

    getApplicationTypeById: async ({
        subAccountId,
        applicationId,
    }: {
        subAccountId: string
        applicationId: string
    }): Promise<ApplicationTypes> => {
        const { data } = await axios.get<{ type: ApplicationTypes }>(
            `/guardian/${subAccountId}/application/applications/${applicationId}/type`,
        )

        return data.type
    },

    getApplicationById: async ({
        subAccountId,
        applicationId,
    }: {
        subAccountId: string
        applicationId: string
    }): Promise<IApplication> => {
        const { data } = await axios.get<IApplication>(
            `/guardian/${subAccountId}/application/applications/${applicationId}`,
        )

        return data
    },

    addInvoiceExpense: async (
        {
            subAccountId,
            applicationId,
            formData,
        }: {
            subAccountId: string
            applicationId: string
            formData: FormData
        },
        config: AxiosRequestConfig = {},
    ): Promise<{ invoiceId: string }> => {
        const { data } = await axios.post<{ invoiceId: string }>(
            `/guardian/${subAccountId}/application/applications/${applicationId}/medical-expense-reimbursement/invoice/invoices`,
            formData,
            config,
        )

        return data
    },

    removeInvoiceExpense: async ({
        invoiceId,
        applicationId,
        subAccountId,
        version,
    }: {
        invoiceId: string
        subAccountId: string
        applicationId: string
        version: number
    }): Promise<boolean> => {
        const { status } = await axios.delete(
            `/guardian/${subAccountId}/application/applications/${applicationId}/medical-expense-reimbursement/invoice/invoices/${invoiceId}`,
            {
                params: {
                    version,
                },
            },
        )

        return status === NO_CONTENT_CODE
    },

    getInvoiceExpenseFileById: async ({
        invoiceId,
        applicationId,
        subAccountId,
    }: {
        invoiceId: string
        subAccountId: string
        applicationId: string
    }): Promise<IAttachmentToDownload> => {
        const { data } = await axios.get<IAttachmentToDownload>(
            `/guardian/${subAccountId}/application/applications/${applicationId}/medical-expense-reimbursement/invoice/invoices/${invoiceId}/download`,
            {
                params: {
                    base64: true,
                },
            },
        )

        return data
    },

    addRoadCard: async (
        {
            applicationId,
            subAccountId,
            formData,
        }: {
            subAccountId: string
            applicationId: string
            formData: FormData
        },
        config: AxiosRequestConfig = {},
    ): Promise<{ roadCardId: string }> => {
        const { data } = await axios.post<{ roadCardId: string }>(
            `/guardian/${subAccountId}/application/applications/${applicationId}/medical-expense-reimbursement/road-card/road-cards`,
            formData,
            config,
        )

        return data
    },

    removeRoadCard: async ({
        applicationId,
        subAccountId,
        roadCardId,
        version,
    }: {
        subAccountId: string
        applicationId: string
        roadCardId: string
        version: number
    }): Promise<boolean> => {
        const { status } = await axios.delete(
            `/guardian/${subAccountId}/application/applications/${applicationId}/medical-expense-reimbursement/road-card/road-cards/${roadCardId}`,
            {
                params: {
                    version,
                },
            },
        )

        return status === NO_CONTENT_CODE
    },

    getRoadCardFileById: async ({
        applicationId,
        subAccountId,
        roadCardId,
    }: {
        subAccountId: string
        applicationId: string
        roadCardId: string
    }): Promise<IAttachmentToDownload> => {
        const { data } = await axios.get<IAttachmentToDownload>(
            `/guardian/${subAccountId}/application/applications/${applicationId}/medical-expense-reimbursement/road-card/road-cards/${roadCardId}/download`,
            {
                params: {
                    base64: true,
                },
            },
        )

        return data
    },

    sendApplicationToFirstApproval: async ({
        applicationId,
        subAccountId,
        version,
    }: {
        subAccountId: string
        applicationId: string
        version: number
    }): Promise<boolean> => {
        const { status } = await axios.put(
            `/guardian/${subAccountId}/application/applications/${applicationId}/change-status`,
            {
                status: ApplicationStatuses.FOR_FIRST_APPROVAL,
                version,
            },
        )

        return status === NO_CONTENT_CODE
    },

    getApplicationDataInPDF: async ({
        applicationId,
        subAccountId,
    }: {
        subAccountId: string
        applicationId: string
    }): Promise<IAttachmentToDownload> => {
        const { data } = await axios.get<IAttachmentToDownload>(
            `/guardian/${subAccountId}/application/applications/${applicationId}/pdf`,
            {
                params: {
                    base64: true,
                },
            },
        )

        return data
    },

    getApplicationSettlement: async ({
        applicationId,
        subAccountId,
    }: {
        subAccountId: string
        applicationId: string
    }): Promise<IApplicationSettlementData> => {
        const { data } = await axios.get<IApplicationSettlementData>(
            `/guardian/${subAccountId}/application/settlement/${applicationId}`,
        )

        return data
    },

    getFinancialSupportDetails: async ({
        applicationId,
        subAccountId,
    }: {
        subAccountId: string
        applicationId: string
    }): Promise<IFinancialSupportDetails> => {
        const { data } = await axios.get<IFinancialSupportDetails>(
            `/guardian/${subAccountId}/application/applications/${applicationId}/financial-support/details`,
        )

        return data
    },

    changeFinancialSupportAmount: async ({
        applicationId,
        subAccountId,
        amount,
        version,
    }: {
        subAccountId: string
        applicationId: string
        amount: number
        version: number
    }): Promise<boolean> => {
        const { status } = await axios.put(
            `/guardian/${subAccountId}/application/applications/${applicationId}/financial-support/change-amount`,
            {
                amount,
                version,
            },
        )

        return status === OK_CODE
    },
})
